import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Search, X, ListChecks } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, StatusBadge, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const TYPE_LABELS = {
  client: 'Categorias de Cliente',
  vehicle: 'Categorias de Veículo',
  financial: 'Categorias Financeiras',
  contract_type: 'Tipos de Contrato',
  document_type: 'Tipos de Documento',
  maintenance_type: 'Tipos de Manutenção',
};
const TYPES = Object.keys(TYPE_LABELS);

const emptyForm = { type: 'client', name: '', code: '', color: '#C52429', description: '', status: 'active' };

export default function Categories() {
  const [currentType, setCurrentType] = useState('client');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit, type: currentType };
      if (search) params.search = search;
      const { data } = await api.get('/categories', { params });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); load(); }, [currentType]); // eslint-disable-line
  useEffect(() => { load(); }, [page]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, type: currentType }); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); setModalOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.put(`/categories/${editing.id}`, form); toast.success('Atualizada'); }
      else { await api.post('/categories', form); toast.success('Criada'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/categories/${deleteId}`); toast.success('Excluída'); setDeleteId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div>
      <PageHeader title="Categorias" subtitle="Cadastros dinâmicos por tipo" onAction={openCreate} actionLabel="Nova Categoria" />

      {/* Tabs de tipos */}
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-2 mb-4">
        <div className="flex flex-wrap gap-1">
          {TYPES.map(t => (
            <button
              key={t}
              onClick={() => setCurrentType(t)}
              className={`px-3 py-2 rounded-lg text-sm transition-colors ${currentType === t ? 'bg-brand text-white' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome ou código..." className="pl-9" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"><X className="h-4 w-4" /></button>}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium">Código</th>
                <th className="text-left px-4 py-3 font-medium">Cor</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={5} className="p-4"><TableSkeleton rows={5} cols={5} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5}><EmptyState icon={ListChecks} title="Nenhuma categoria neste tipo" description="Adicione categorias para organizar seus cadastros." /></td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="tr-hover">
                  <td className="px-4 py-3"><div className="font-medium">{c.name}</div>{c.description && <div className="text-xs text-neutral-500">{c.description}</div>}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-neutral-600 dark:text-neutral-400">{c.code || '-'}</span></td>
                  <td className="px-4 py-3">{c.color ? <div className="flex items-center gap-2"><span className="inline-block h-4 w-4 rounded" style={{ background: c.color }} /><span className="font-mono text-xs">{c.color}</span></div> : '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status === 'active' ? 'Ativo' : 'Inativo'} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
            <DialogDescription>Categorias são cadastros configuráveis pelo usuário.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Cor</Label>
              <div className="flex gap-2">
                <input type="color" value={form.color || '#C52429'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-14 rounded border border-neutral-300 dark:border-neutral-700 cursor-pointer" />
                <Input value={form.color || ''} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="#C52429" className="font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Ativo</SelectItem><SelectItem value="inactive">Inativo</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Descrição</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>Soft delete.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
